import { shallowEqual } from 'react-redux';

import { MediaText } from 'editor/src/store/design/types';
import {
  buildTextStyleElementUpdate,
  getTextStyle,
  isTextStyleProtected,
} from 'editor/src/store/design/util/textStyles';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import applyToSelectedMediaTypeOperation from './applyToSelectedMediaTypeOperation';
import loadTextStyleFontOperation from './loadTextStyleFontOperation';

import type { TextStyleUpdateResult } from './updateTextStyleOperation';

/**
 * Makes the selected texts follow a style (taking on its look), or — with no
 * style — stop following one while keeping the look they have. One undo step.
 * Resolves `failed` when the style's font could not be loaded and nothing changed.
 */
const assignTextStyleToSelectionOperation =
  (styleId: string | undefined) =>
  async (dispatch: ThunkDispatch, getState: () => RootState): Promise<TextStyleUpdateResult> => {
    if (!styleId) {
      dispatch(
        applyToSelectedMediaTypeOperation('text', function detachTextStyle(element: MediaText) {
          if (isTextStyleProtected(getState(), element)) {
            return undefined;
          }
          return element.extra.textStyleId ? { extra: { ...element.extra, textStyleId: undefined } } : undefined;
        }),
      );
      return 'applied';
    }

    const style = getTextStyle(getState().design.designData, styleId);
    const selection = getState().editor.selectedElementUuids;
    if (!style) {
      return 'skipped';
    }
    if (!(await dispatch(loadTextStyleFontOperation(style.fontFamily)))) {
      return 'failed';
    }
    // The author picked the style for what was selected then; if the selection moved
    // while the font loaded, applying it now would restyle the wrong texts.
    if (!shallowEqual(selection, getState().editor.selectedElementUuids)) {
      return 'skipped';
    }
    // Read again: the style may have changed while its font loaded.
    const current = getTextStyle(getState().design.designData, styleId);
    if (!current) {
      return 'skipped';
    }
    dispatch(
      applyToSelectedMediaTypeOperation('text', function assignTextStyle(element: MediaText) {
        return isTextStyleProtected(getState(), element) ? undefined : buildTextStyleElementUpdate(element, current);
      }),
    );
    return 'applied';
  };

export default assignTextStyleToSelectionOperation;
